import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography, Skeleton, useTheme, useMediaQuery } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import OrderForm from './OrderForm';
import OrderBook from './OrderBook';
import TradeTabs from './TradeTabs';
import TradingViewWidget from './TradingViewChart';
import CoinDetails from './CoinDetails';
import { fetchSpotTradingPairs, fetchWalletIds } from '../../store/Slices/assetSlice';
import { setSelectedCoin } from '../../store/Slices/spotTradingSlice';
import TopNav from '../../Components/NavComponents/TopNav';
import BottomNav from '../../Components/NavComponents/BottomNav';

const Spot = () => {
  const dispatch = useDispatch();
  const { spotTradingPairs, walletIds } = useSelector((state) => state.assets);
  const { selectedCoin, tradeHistoryLoading, openOrdersLoading } = useSelector((state) => state.spotTrading);
  const { data: user, loading: userLoading } = useSelector((state) => state.user);

  const [tradingPairsLoading, setTradingPairsLoading] = useState(false);
  const [tradingPairsError, setTradingPairsError] = useState(null);
  const [walletIdsLoading, setWalletIdsLoading] = useState(false);
  const [walletIdsError, setWalletIdsError] = useState(null);
  const [hasFetchedInitialData, setHasFetchedInitialData] = useState(false);
  const [walletIdsRetryCount, setWalletIdsRetryCount] = useState(0);
  const maxRetries = 3;

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    console.log('Spot.js useEffect triggered for initial data fetch', {
      tradingPairsLoading,
      walletIdsLoading,
      hasFetchedInitialData,
      walletIdsRetryCount,
      userId: user?._id,
      userLoading,
    });

    const fetchInitialData = async () => {
      if (hasFetchedInitialData || userLoading) return;

      // Check token validity
      const token = localStorage.getItem('token');
      if (!token) {
        setTradingPairsError('No token found. Please log in.');
        setWalletIdsError('No token found.');
        setHasFetchedInitialData(true);
        window.location.href = '/login';
        return;
      }

      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
          localStorage.removeItem('token');
          setTradingPairsError('Token expired. Please log in again.');
          setWalletIdsError('Token expired.');
          setHasFetchedInitialData(true);
          window.location.href = '/login';
          return;
        }
      } catch (error) {
        console.error('Invalid token:', error);
        localStorage.removeItem('token');
        setTradingPairsError('Invalid token. Please log in again.');
        setWalletIdsError('Invalid token.');
        setHasFetchedInitialData(true);
        window.location.href = '/login';
        return;
      }

      // Ensure user._id is available
      if (!user?._id) {
        setTradingPairsError('User data not loaded. Please wait or log in.');
        setWalletIdsError('User data not loaded.');
        setHasFetchedInitialData(true);
        return;
      }

      setHasFetchedInitialData(true);

      // Check if data is already in Redux
      if (spotTradingPairs.length > 0 && walletIds.spotWalletId) {
        console.log('Using persisted spotTradingPairs and walletIds');
        setTradingPairsError(null);
        setWalletIdsError(null);
        return;
      }

      // Fetch spot trading pairs
      setTradingPairsLoading(true);
      try {
        const response = await dispatch(fetchSpotTradingPairs()).unwrap();
        console.log('Spot trading pairs fetched successfully:', response);
        setTradingPairsError(null);
      } catch (err) {
        const errorMessage = err === 'Unauthorized' ? 'Please log in again.' : 'Failed to fetch spot trading pairs.';
        setTradingPairsError(errorMessage);
        console.error('Error fetching spot trading pairs:', err);
        if (err === 'Unauthorized') {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      } finally {
        setTradingPairsLoading(false);
      }

      // Fetch wallet IDs with retry
      const fetchWalletIdsWithRetry = async () => {
        setWalletIdsLoading(true);
        try {
          const walletIdsData = await dispatch(fetchWalletIds({userId : user._id})).unwrap();
          console.log('Wallet IDs fetched successfully:', walletIdsData);
          setWalletIdsError(null);
          setWalletIdsRetryCount(0);
        } catch (err) {
          console.error('Error fetching wallet IDs:', {
            message: err.message,
            status: err.response?.status,
            data: err.response?.data,
          });

          if (err.response?.status === 401 || err === 'Unauthorized') {
            setWalletIdsError('Unauthorized: Please log in again.');
            localStorage.removeItem('token');
            window.location.href = '/login';
          } else if (walletIdsRetryCount < maxRetries) {
            console.log(`Retrying fetch wallet IDs (${walletIdsRetryCount + 1}/${maxRetries})...`);
            setWalletIdsRetryCount((prev) => prev + 1);
            setTimeout(fetchWalletIdsWithRetry, 2000);
          } else {
            setWalletIdsError(
              err.response?.data?.message ||
                'Failed to fetch wallet IDs. Please check your connection or try again later.'
            );
          }
        } finally {
          setWalletIdsLoading(false);
        }
      };

      fetchWalletIdsWithRetry();
    };

    fetchInitialData();
  }, [dispatch, hasFetchedInitialData, user?._id, userLoading, spotTradingPairs, walletIds]);

  const defaultCoin = useMemo(() => {
    if (spotTradingPairs.length > 0) {
      return spotTradingPairs[0]?.symbol; // e.g., "BTCUSDT"
    }
    return null;
  }, [spotTradingPairs]);

  useEffect(() => {
    console.log('Spot.js useEffect triggered for setting default coin', { defaultCoin, selectedCoin });
    if (defaultCoin && !selectedCoin) {
      console.log('Setting default selected coin:', defaultCoin);
      dispatch(setSelectedCoin(defaultCoin));
    }
  }, [dispatch, defaultCoin, selectedCoin]);

  const spotAssets = useMemo(() => {
    return spotTradingPairs.map((pair) => ({
      symbol: pair.base_asset_id?.symbol,
      name: pair.base_asset_id?.name,
    }));
  }, [spotTradingPairs]);

  const handleRetry = (type) => {
    if (type === 'tradingPairs') {
      setTradingPairsLoading(true);
      setTradingPairsError(null);
      dispatch(fetchSpotTradingPairs())
        .unwrap()
        .then(() => setTradingPairsError(null))
        .catch((err) => {
          const errorMessage = err === 'Unauthorized' ? 'Please log in again.' : 'Failed to fetch spot trading pairs.';
          setTradingPairsError(errorMessage);
          if (err === 'Unauthorized') {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
        })
        .finally(() => setTradingPairsLoading(false));
    } else if (type === 'walletIds') {
      setWalletIdsLoading(true);
      setWalletIdsError(null);
      setWalletIdsRetryCount(0);
      dispatch(fetchWalletIds({userId: user._id}))
        .unwrap()
        .then(() => setWalletIdsError(null))
        .catch((err) => {
          setWalletIdsError(
            err.response?.data?.message || 'Failed to fetch wallet IDs'
          );
          if (err.response?.status === 401 || err === 'Unauthorized') {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
        })
        .finally(() => setWalletIdsLoading(false));
    }
  };

  const isLoading = tradingPairsLoading || walletIdsLoading || userLoading;
  const hasCriticalError = tradingPairsError && walletIdsError;

  if (hasCriticalError) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: 2, textAlign: 'center' }}>
        <Typography sx={{ color: 'error.main', mb: 2 }}>
          Error: Failed to load essential data
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setHasFetchedInitialData(false);
            setWalletIdsRetryCount(0);
            setTradingPairsError(null);
            setWalletIdsError(null);
          }}
        >
          Retry All
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }}
          sx={{ ml: 2 }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        {!isSmallScreen && <TopNav />}
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mb: isSmallScreen ? '60px' : 0 }}>
          <Box sx={{ width: '100%', minHeight: '880px', padding: '7px 1px', mt: !isSmallScreen ? 8 : 0 }}>
            <Box sx={{ width: '100%', minHeight: '500px' }}>
              {tradingPairsLoading ? (
                <Skeleton variant="rectangular" height={56} sx={{ mb: 4 }} />
              ) : tradingPairsError ? (
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: 'error.main', mb: 2 }}>
                    {tradingPairsError}
                  </Typography>
                  <Button variant="contained" color="primary" onClick={() => handleRetry('tradingPairs')}>
                    Retry
                  </Button>
                </Box>
              ) : (
                <CoinDetails spotAssets={spotAssets} />
              )}

              {isLargeScreen && (
                <Box sx={{ display: 'flex', width: '100%', height: '650px', mt: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
                  <Box sx={{ width: '80%', display: 'flex', mr: '1px' }}>
                    <Box sx={{ width: '75%', overflow: 'hidden' }}>
                      {isLoading ? <Skeleton variant="rectangular" height={650} /> : <TradingViewWidget />}
                    </Box>
                    <Box sx={{ width: '25%', overflow: 'hidden' }}>
                      {selectedCoin ? <OrderBook /> : <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>Select a coin to view the order book</Typography>}
                    </Box>
                  </Box>
                  <Box sx={{ width: '20%', overflow: 'hidden' }}>
                    {selectedCoin ? <OrderForm /> : <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>Select a coin to place an order</Typography>}
                  </Box>
                </Box>
              )}

              {isMediumScreen && (
                <Box sx={{ width: '100%', mt: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', width: '100%', mb: '10px', overflow: 'hidden' }}>
                    <Box sx={{ width: '70%', overflow: 'hidden' }}>
                      {isLoading ? <Skeleton variant="rectangular" height={400} /> : <TradingViewWidget />}
                    </Box>
                    <Box sx={{ width: '30%', overflow: 'hidden' }}>
                      {selectedCoin ? <OrderForm /> : <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>Select a coin to place an order</Typography>}
                    </Box>
                  </Box>
                  <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    {selectedCoin ? <OrderBook /> : <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>Select a coin to view the order book</Typography>}
                  </Box>
                </Box>
              )}

              {isSmallScreen && (
                <Box sx={{ width: '100%', mt: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
                  <Box sx={{ width: '100%', display: 'flex', overflow: 'hidden' }}>
                    <Box sx={{ width: '50%', overflow: 'hidden' }}>
                      {selectedCoin ? <OrderForm /> : <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>Select a coin to place an order</Typography>}
                    </Box>
                    <Box sx={{ width: '50%', overflow: 'hidden' }}>
                      {selectedCoin ? <OrderBook /> : <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>Select a coin to view the order book</Typography>}
                    </Box>
                  </Box>
                </Box>
              )}

              {selectedCoin && (
                <Box sx={{ width: '100%', mt: 2 }}>
                  {walletIdsLoading ? (
                    <Skeleton variant="rectangular" height={200} />
                  ) : walletIdsError ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ color: 'error.main', mb: 2 }}>
                        {walletIdsError}
                      </Typography>
                      <Button variant="contained" color="primary" onClick={() => handleRetry('walletIds')}>
                        Retry
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          localStorage.removeItem('token');
                          window.location.href = '/login';
                        }}
                        sx={{ ml: 2 }}
                      >
                        Go to Login
                      </Button>
                    </Box>
                  ) : (
                    <TradeTabs />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {isSmallScreen && <BottomNav />}
    </>
  );
};

export default Spot;